import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, MoreHorizontal, Send, UserMinus, ShieldCheck, Copy, FlaskConical } from "lucide-react";
import { fmtDate } from "@/lib/status";

const initials = (name = "") =>
  name.split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "U";

const roleBadge = {
  owner: "bg-primary text-primary-foreground",
  admin: "bg-[hsl(var(--status-upcoming)/0.12)] text-[hsl(var(--status-upcoming))]",
  member: "bg-muted text-muted-foreground",
};

export const TeamTab = () => {
  const { user } = useAuth();
  const canManage = ["owner", "admin"].includes(user?.role);
  const isOwner = user?.role === "owner";
  const [members, setMembers] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [devAcceptUrl, setDevAcceptUrl] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);

  const load = useCallback(async () => {
    const reqs = [api.get("/team/members")];
    if (canManage) reqs.push(api.get("/team/invitations"));
    const [membersRes, invRes] = await Promise.all(reqs);
    setMembers(membersRes.data.members);
    if (invRes) setInvitations(invRes.data.invitations);
  }, [canManage]);

  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    setInviteBusy(true);
    try {
      const res = await api.post("/team/invitations", { email: inviteEmail, role: inviteRole });
      toast.success(res.data.message);
      setDevAcceptUrl(res.data.dev_accept_url || null);
      setInviteEmail("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setInviteBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/team/invitations/${id}`);
      toast.success("Invitation revoked.");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const changeRole = async (member, role) => {
    try {
      const res = await api.patch(`/team/members/${member.id}`, { role });
      toast.success(res.data.message);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const removeMember = async () => {
    try {
      const res = await api.delete(`/team/members/${removeTarget.id}`);
      toast.success(res.data.message);
      setRemoveTarget(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
      setRemoveTarget(null);
    }
  };

  if (!members) {
    return <div className="space-y-6"><Skeleton className="h-40" /><Skeleton className="h-64" /></div>;
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Invite a teammate</CardTitle>
            <CardDescription>They'll get an email with a link to join {user?.org?.name}.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={invite} className="flex flex-col sm:flex-row gap-3">
              <Input type="email" required placeholder="teammate@company.com" value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)} className="flex-1 rounded-lg"
                data-testid="team-invite-email-input" />
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="w-full sm:w-[130px] rounded-lg" data-testid="team-invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" disabled={inviteBusy} className="rounded-xl" data-testid="team-invite-submit-button">
                {inviteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Invite</>}
              </Button>
            </form>
            <p className="mt-3 text-xs text-muted-foreground">
              Admins manage items, categories and the team. Members can add, view and edit items.
            </p>
            {devAcceptUrl && (
              <div className="mt-4 rounded-lg border border-dashed p-3 text-xs text-muted-foreground flex items-center gap-2">
                <FlaskConical className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate flex-1">Dev shortcut: {devAcceptUrl}</span>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" aria-label="Copy invite link"
                  onClick={() => { navigator.clipboard?.writeText(devAcceptUrl); toast.success("Link copied."); }}
                  data-testid="team-invite-dev-copy-button">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {canManage && invitations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Pending invitations</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border" data-testid="pending-invitations-list">
              {invitations.map((inv) => (
                <li key={inv.id} className="flex items-center justify-between py-3 gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{inv.email}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Invited as {inv.role} · {inv.expired ? "expired" : `expires ${fmtDate(inv.expires_at?.slice(0, 10))}`}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" className="text-muted-foreground rounded-lg shrink-0"
                    onClick={() => revoke(inv.id)} data-testid={`invitation-revoke-${inv.id}`}>
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Team members</CardTitle>
          <CardDescription>{members.length} {members.length === 1 ? "person" : "people"} in {user?.org?.name}.</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="divide-y divide-border" data-testid="team-members-list">
            {members.map((m) => {
              const isSelf = m.id === user.id;
              const canActOn = canManage && !isSelf && m.role !== "owner" && !(user.role === "admin" && m.role === "admin");
              return (
                <li key={m.id} className="flex items-center justify-between py-3 gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar className="h-9 w-9 shrink-0">
                      <AvatarFallback className="bg-secondary text-foreground text-xs font-semibold">{initials(m.name)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {m.name} {isSelf && <span className="text-muted-foreground font-normal">(you)</span>}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={`${roleBadge[m.role]} capitalize rounded-full border-0`}>{m.role}</Badge>
                    {canActOn && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Actions for ${m.name}`}
                            data-testid={`member-actions-${m.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {isOwner && m.role === "member" && (
                            <DropdownMenuItem onClick={() => changeRole(m, "admin")} data-testid={`member-make-admin-${m.id}`}>
                              <ShieldCheck className="h-4 w-4 mr-2" /> Make admin
                            </DropdownMenuItem>
                          )}
                          {isOwner && m.role === "admin" && (
                            <DropdownMenuItem onClick={() => changeRole(m, "member")} data-testid={`member-make-member-${m.id}`}>
                              <ShieldCheck className="h-4 w-4 mr-2" /> Make member
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => setRemoveTarget(m)} className="text-[hsl(var(--destructive))]"
                            data-testid={`member-remove-${m.id}`}>
                            <UserMinus className="h-4 w-4 mr-2" /> Remove from team
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      <AlertDialog open={Boolean(removeTarget)} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              They'll immediately lose access to {user?.org?.name}. Items they added stay with the organization.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeMember} className="rounded-xl bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90"
              data-testid="member-remove-confirm-button">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
